// Identity-mapping contract interfaces (FD-098).
//
// These mirror the FD-097 / FD-102 / FD-103 rows in the API Contract Changes
// table in docs/TASKS.md EXACTLY. Every interface is doc-commented with the
// owning contract row. When a contract row changes, the matching interface
// must be updated in the same PR (frontend/CLAUDE.md data-synchronisation rule).

// ---------------------------------------------------------------------------
// Tenant users — GET /v1/migrations/{id}/tenant-users
// Contract: FD-097 v1.0 row + FD-103 v1.1 amendment (additive onPrem fields
// + D2C per-type semantics).
// ---------------------------------------------------------------------------

/**
 * One Graph user row returned by the tenant-users picker read and embedded in
 * identity-suggestion candidates / resolved source users.
 *
 * FD-097 v1.0: `{id, displayName, userPrincipalName, mail, accountEnabled}`.
 * FD-103 v1.1 (additive): `onPremisesSamAccountName`,
 * `onPremisesSecurityIdentifier`, `mailNickname` — documented `null` for
 * cloud-only users.
 */
export interface TenantUser {
  id: string;
  displayName: string | null;
  userPrincipalName: string | null;
  mail: string | null;
  accountEnabled: boolean;
  onPremisesSamAccountName: string | null;
  onPremisesSecurityIdentifier: string | null;
  mailNickname: string | null;
  /**
   * FD-506. Optional because they are additive — a Manager deployed before
   * FD-506 omits them, and every consumer must render their absence rather
   * than an empty string. Null when the user simply has none set.
   *
   * They exist to tell two people with the same display name apart faster
   * than a UPN can (the FD-505 scenario). `manager` and group membership are
   * deliberately NOT here: both cost an extra Graph call per user.
   */
  department?: string | null;
  jobTitle?: string | null;
}

/** Which tenant a picker search runs against (FD-097 v1.0 `tenant` query param). */
export type TenantRole = 'source' | 'destination';

/**
 * Unwrapped `data` payload of GET /v1/migrations/{id}/tenant-users
 * (FD-097 v1.0 row): `data.{tenantRole, tenantId, users[], skipToken|null}`.
 * `skipToken` is an opaque Graph continuation — never parsed client-side.
 */
export interface TenantUsersData {
  tenantRole: TenantRole;
  tenantId: string;
  users: TenantUser[];
  skipToken: string | null;
}

// ---------------------------------------------------------------------------
// Identity mappings — GET/POST /v1/migrations/{id}/identity-mappings[...]
// Contract: FD-097 v1.0 rows + FD-103 v1.1 amendments.
// ---------------------------------------------------------------------------

/**
 * Mapping list/point-read view row.
 *
 * FD-097 v1.0 list row `{sourceUserId, sourceUserPrincipalName,
 * sourceDisplayName, destUserId, destUserPrincipalName, destDisplayName,
 * validatedAt, createdAt, updatedAt, createdBy}` extended by FD-103 v1.1:
 * additive `sourceUserSid` (string|null) and `sourceSamAccountName`
 * (string|null); `sourceUserId` becomes NULLABLE (`null` on D2C rows, whose
 * row key is the normalized SID). D2C `sourceUserPrincipalName` /
 * `sourceDisplayName` are also null unless enriched.
 */
export interface IdentityMappingView {
  sourceUserId: string | null;
  sourceUserPrincipalName: string | null;
  sourceDisplayName: string | null;
  destUserId: string;
  destUserPrincipalName: string | null;
  destDisplayName: string | null;
  // FD-357 / FD-ADR-036 §4 — whether this mapping would pass the delivery-time
  // identity gate: destUserId present and a GUID. Computed SERVER-SIDE (one
  // authority — the gate's own predicate); the UI renders it, never re-derives
  // it. Optional: additive; a Manager deployed before FD-357 omits it.
  destinationReady?: boolean;
  validatedAt: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  sourceUserSid: string | null;
  sourceSamAccountName: string | null;
}

/**
 * Unwrapped `data` payload of GET /v1/migrations/{id}/identity-mappings
 * (FD-097 v1.0 row; FD-103 v1.1 extends the row shape, not the envelope):
 * `data.{migrationId, mappings[], continuationToken|null}`.
 */
export interface IdentityMappingsListData {
  migrationId: string;
  mappings: IdentityMappingView[];
  continuationToken: string | null;
}

/**
 * T2T submit row for POST /v1/migrations/{id}/identity-mappings.
 * FD-103 v1.1: `{sourceUserId: <guid>, destUserId: <guid>,
 * sourceUserSid?: <sid>}` — `sourceUserSid` is optional provenance (FD-098
 * passes the manifest SID through when accepting a suggestion).
 */
export interface T2TMappingSubmission {
  sourceUserId: string;
  destUserId: string;
  sourceUserSid?: string;
}

/**
 * D2C submit row for POST /v1/migrations/{id}/identity-mappings.
 * FD-103 v1.1: `{sourceUserSid: <sid>, destUserId: <guid>}` — `sourceUserId`
 * must be ABSENT (per-row reject if present).
 */
export interface D2CMappingSubmission {
  sourceUserSid: string;
  destUserId: string;
}

export type MappingSubmission = T2TMappingSubmission | D2CMappingSubmission;

/** Request body of POST /v1/migrations/{id}/identity-mappings (FD-097 v1.0). */
export interface SetIdentityMappingsRequest {
  mappings: MappingSubmission[];
}

/**
 * Rejected-row echo in the set-identity-mappings buckets. FD-103 v1.1:
 * `{sourceUserId, reason}` for T2T rows (byte-identical to FD-097 v1.0) /
 * `{sourceUserSid, reason}` for D2C rows.
 */
export interface RejectedMapping {
  sourceUserId?: string;
  sourceUserSid?: string;
  reason: string;
}

/**
 * Unwrapped `data` payload of POST /v1/migrations/{id}/identity-mappings.
 * FD-097 v1.0: 200 all accepted / 207 partial / 409 all rejected with
 * `data.{migrationId, accepted[], updated[], rejected[]}`. FD-103 v1.1:
 * `accepted[]`/`updated[]` echo NORMALIZED row keys (lowercase source
 * objectId GUID for T2T; uppercased-S SID for D2C).
 */
export interface SetIdentityMappingsData {
  migrationId: string;
  accepted: string[];
  updated: string[];
  rejected: RejectedMapping[];
  // Summary line, present on success/partial results per the add-devices
  // precedent; moves to the envelope's error.message on full rejection.
  message?: string;
}

/**
 * `data` of GET /v1/migrations/{id}/identity-mappings/template (FD-099).
 * `csv` is the header row + one placeholder example row, matching the migration
 * type's mapping schema (T2T: sourceUserId,destUserId,sourceUserSid — last
 * optional; D2C: sourceUserSid,destUserId). The console offers it as a download.
 */
export interface IdentityMappingTemplateData {
  migrationId: string;
  migrationType: string;
  filename: string;
  csv: string;
}

/**
 * Request body of POST /v1/migrations/{id}/identity-mappings/remove.
 * FD-103 v1.1: field renamed `sourceUserIds` → `sourceKeys` (array of GUIDs
 * and/or SIDs; invalid-shape keys land in `notFound`). No alias accepted.
 */
export interface RemoveIdentityMappingsRequest {
  sourceKeys: string[];
}

/**
 * Unwrapped `data` payload of POST /v1/migrations/{id}/identity-mappings/remove.
 * FD-097 v1.0: 200 all removed / 207 partial / 404 nothing removed;
 * `data.{migrationId, removed[], notFound[]}` — FD-103 v1.1: buckets echo
 * normalized keys.
 */
export interface RemoveIdentityMappingsData {
  migrationId: string;
  removed: string[];
  notFound: string[];
  message?: string;
}

// ---------------------------------------------------------------------------
// Identity suggestions — GET /v1/migrations/{id}/identity-suggestions
// Contract: FD-103 v1.0 row.
// ---------------------------------------------------------------------------

/**
 * SID classification (FD-103 v1.0): `S-1-12-1` → `entraObjectId` (offline
 * objectId decode), `S-1-5-21` → `onPremSid` (Graph
 * onPremisesSecurityIdentifier filter), other formats → `unknown`
 * (needsAttention, no Graph work).
 */
export type SidType = 'entraObjectId' | 'onPremSid' | 'unknown';

/**
 * Destination-candidate ranking tier that produced a suggestion (FD-103 v1.0):
 * `sidExact` → `samAccountName` → `userPrincipalName` (local-part prefix) →
 * `mail` → `mailNickname`; first non-empty tier wins.
 */
export type SuggestionMatchedOn =
  | 'sidExact'
  | 'samAccountName'
  | 'userPrincipalName'
  | 'mail'
  | 'mailNickname';

/** One ranked destination candidate: `suggestions[{user, matchedOn}]` (FD-103 v1.0). */
export interface SuggestionCandidate {
  user: TenantUser;
  matchedOn: SuggestionMatchedOn;
}

/**
 * One unique source user, deduped across devices by normalized
 * `PrimaryUserSid` (FD-103 v1.0 `users[]` row): `{sid, sidType, userName?,
 * samName?, displayName?, deviceCount, serialNumbers[], needsAttention,
 * needsAttentionReason?, resolvedSourceUser?, suggestions[],
 * autoAcceptEligible, existingMapping?}`.
 *
 * `autoAcceptEligible` is the SERVER-computed bulk-accept verdict (exactly one
 * suggestion, `sidExact`, `accountEnabled`, no existing mapping) — the
 * frontend renders it, never recomputes it.
 */
export interface IdentitySuggestionUser {
  sid: string;
  sidType: SidType;
  userName?: string | null;
  samName?: string | null;
  displayName?: string | null;
  deviceCount: number;
  serialNumbers: string[];
  needsAttention: boolean;
  needsAttentionReason?: string | null;
  resolvedSourceUser?: TenantUser | null;
  suggestions: SuggestionCandidate[];
  autoAcceptEligible: boolean;
  existingMapping?: IdentityMappingView | null;
}

/**
 * Unwrapped `data` payload of GET /v1/migrations/{id}/identity-suggestions
 * (FD-103 v1.0): `data.{migrationId, migrationType, users[],
 * continuationToken|null, devicesWithoutManifest, totalUniqueUsers}`.
 * Devices with null `PrimaryUserSid` are excluded from `users[]` and counted
 * in `devicesWithoutManifest`. `continuationToken` is an opaque base64
 * wrapper over deterministic SID-ordered paging.
 */
/**
 * FD-506 — campaign-wide bucket counts, computed server-side over the whole
 * deduped user set crossed with the whole mappings partition, with ZERO Graph
 * calls. This is what lets the tiles stop counting "whatever happens to be
 * loaded".
 *
 * `suggested` and `needsAttention` are ABSENT ON PURPOSE. Both are products of
 * the per-page Graph work, so no campaign-wide figure exists for them; the
 * console renders those two as loaded-scoped and says so on the tile. A tile
 * row that mixed the two scopes silently would be the worst of both.
 */
export interface IdentitySummary {
  totalUniqueUsers: number;
  mapped: number;
  unmapped: number;
  destinationNotReady: number;
  unsupportedSid: number;
}

export interface IdentitySuggestionsData {
  migrationId: string;
  migrationType: string;
  users: IdentitySuggestionUser[];
  continuationToken: string | null;
  devicesWithoutManifest: number;
  totalUniqueUsers: number;
  /** How many unique users matched `search`; equals `totalUniqueUsers` when unsearched. */
  matchedUserCount?: number;
  /** Absent from a Manager predating FD-506 — callers fall back to loaded-scoped counts. */
  summary?: IdentitySummary;
}

// ---------------------------------------------------------------------------
// Consent-class 409 payload — FD-096 shape, surfaced by tenant-users,
// set-identity-mappings, and identity-suggestions.
// ---------------------------------------------------------------------------

/** Envelope `error.code` values that carry the FD-096 consent payload. */
export const CONSENT_ERROR_CODES = [
  'TENANT_NOT_CONSENTED',
  'TENANT_CONSENT_DRIFT',
] as const;
export type ConsentErrorCode = (typeof CONSENT_ERROR_CODES)[number];

/**
 * `data` payload of a consent-class 409 (FD-096 shape, per the FD-097/FD-103
 * rows): `data.{tenantId, failureClass, missingRoles, consentUrl}` — never a
 * raw Graph 403. The UI renders the tenant, the missing roles, and the
 * consent URL as an actionable warning; it never shows the raw error string.
 */
export interface ConsentErrorData {
  tenantId: string;
  failureClass: string;
  missingRoles: string[];
  consentUrl: string;
}
